import { trackProductInfoProductDescriptionAccordionExpandAboutThisProductSectionClick } from "@faire/web-api/events/productInfo/click/productDescriptionAccordionExpandAboutThisProductSection";
import { IProductInformationSection } from "@faire/web-api/indigofair/data/IProductInformationSection";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Button } from "@faire/web/slate/Button";
import { Globe } from "@faire/web/slate/icons/foundation/Globe";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useState } from "@faire/web/ui/hooks/useState";
import { observer } from "mobx-react";
import React from "react";

import { transformListOfItemsInMultiLinedText } from "./__internal__/utils";
import { AccordionContainer } from "./AccordionContainer";

type Props = ProductDescriptionAccordion.IProps;

const INITIAL_NUMBER_OF_LINES_SHOWN = 5;

const ProductDescriptionAccordionBase: React.FC<Props> = ({
  descriptionInformationSection,
}) => {
  const { strictLocalize } = useStrictLocalization();

  const [showOriginal, setShowOriginal] = useState(false);

  const title = strictLocalize({
    defaultMessage: "About this product",
    description: {
      text: "section title. Text of button to expand the description of the product",
    },
  });
  const entries = descriptionInformationSection?.entries;

  const getTranslatedDescription = () => {
    const translatedEntries =
      entries
        ?.map((entry) => entry.translated ?? entry.original)
        .filter(isNotUndefined) ?? [];

    return transformListOfItemsInMultiLinedText(
      translatedEntries,
      descriptionInformationSection?.rendering_format
    );
  };

  const translatedDescription = getTranslatedDescription();
  if (!translatedDescription) {
    return null;
  }

  const getOriginalDescription = () => {
    const originalEntries =
      entries?.map((entry) => entry.original).filter(isNotUndefined) ?? [];

    return transformListOfItemsInMultiLinedText(
      originalEntries,
      descriptionInformationSection?.rendering_format
    );
  };

  const originalDescription = getOriginalDescription();

  const toggleLanguage = (e: React.MouseEvent) => {
    e.stopPropagation();

    setShowOriginal((value) => !value);
  };

  const getToggleText = () => {
    if (showOriginal) {
      return strictLocalize({
        defaultMessage: "Translate",
        description: {
          text: "Button to let user see translated text",
        },
      });
    }

    return strictLocalize({
      defaultMessage: "Show Original Language",
      description: {
        text: "Button to let user see original, untranslated text",
      },
    });
  };

  const getAuxiliarTranslationText = () => {
    if (showOriginal) {
      return strictLocalize({
        defaultMessage: "This info is in its original language.",
      });
    }

    return strictLocalize({
      defaultMessage: "This info has been automatically translated.",
    });
  };

  const renderToggleLanguageButton = () => {
    if (!originalDescription) {
      return undefined;
    }

    return (
      <>
        <Spacer height={12} />
        <Row gap="1x">
          <Globe />
          <Typography>
            {getAuxiliarTranslationText()}{" "}
            <Button variant="tertiary" onClick={toggleLanguage}>
              {getToggleText()}
            </Button>
          </Typography>
        </Row>
      </>
    );
  };

  const getDescription = () => {
    if (originalDescription === null) {
      return translatedDescription;
    }

    return showOriginal ? originalDescription : translatedDescription;
  };

  return (
    <AccordionContainer
      title={title}
      text={getDescription()}
      maxNumberOfLinesShownWhenCollapsed={
        descriptionInformationSection?.max_lines ??
        INITIAL_NUMBER_OF_LINES_SHOWN
      }
      visibleComponent={renderToggleLanguageButton()}
      onExpandAccordionItem={
        trackProductInfoProductDescriptionAccordionExpandAboutThisProductSectionClick
      }
    />
  );
};

export const ProductDescriptionAccordion = observer(
  ProductDescriptionAccordionBase
);

export namespace ProductDescriptionAccordion {
  export interface IProps {
    className?: string;
    descriptionInformationSection: IProductInformationSection | undefined;
  }
}
